import JSZip from 'jszip'
import {
  CREATIVE_PLACEMENTS,
  JPEG_EXPORT_QUALITY,
  SLIDESHOW_TEMPLATES,
  getPlacementSpec,
  type CreativeImageFormat,
  type CreativeAnimation,
  type CreativeAnimationDirection,
  type CreativeAnimationSettings,
  type CreativePlacementId,
  type CreativeTemplateId,
} from '../constants/creativeAssetSpecs'
import {canvasToOpaquePng} from './opaquePng'

export type CreativeBackgroundMode = 'color' | 'gradient' | 'image'

export interface CreativeAssetState {
  template: CreativeTemplateId
  bgMode: CreativeBackgroundMode
  bgColor: string
  bgFrom: string
  bgTo: string
  bgAngle: number
  bgImage: HTMLImageElement | null
  keyArt: HTMLImageElement | null
  keyArtZoom: number
  keyArtOffsetX: number
  keyArtOffsetY: number
  screenshots: HTMLImageElement[]
  icon: HTMLImageElement | null
  headline: string
  subline: string
  textColor: string
  /** 'auto' keeps the template's own placement */
  textAlign: CreativeTextAlign
  textVertical: CreativeTextVertical
  headlineScale: number
  sublineScale: number
  headlineWeight: CreativeTextWeight
}

/** A state change: a partial state, or a function of the latest state (safe for async image loads). */
export type CreativeStatePatch =
  | Partial<CreativeAssetState>
  | ((prev: CreativeAssetState) => Partial<CreativeAssetState>)

export type CreativeTextAlign = 'auto' | 'left' | 'center' | 'right'
export type CreativeTextVertical = 'auto' | 'top' | 'middle' | 'bottom'
export type CreativeTextWeight = 'normal' | 'bolder' | 'bold'

const TEXT_WEIGHTS: Record<CreativeTextWeight, number> = {normal: 400, bolder: 600, bold: 700}

/** One point of a seamless video loop: `t` ∈ [0, 1) is the position in the loop. */
export interface MotionFrame {
  t: number
  animation: CreativeAnimationSettings
}

/** Centre-anchored layout box (the placement's safe area): x ∈ [-1, 1] and y ∈ [-0.5, 0.5] in design units, `unit` px each. */
interface LayoutBox {
  cx: number
  cy: number
  unit: number
  /** Full canvas size in px — layouts that bleed to the edges (strip, gameplay) use it */
  canvasW: number
  canvasH: number
}

type TemplatePainter = (
  ctx: CanvasRenderingContext2D,
  box: LayoutBox,
  state: CreativeAssetState,
  frame?: MotionFrame,
) => void

const FONT_FAMILY = "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const SAFE_AREA_FILL = 0.92
const FALLBACK_BACKGROUND = '#1a1230'
const ICON_CORNER_RATIO = 0.2237
const SCREENSHOT_CORNER_RATIO = 0.06
const HEADLINE_SIZE = 0.15
const SUBLINE_SIZE = 0.065
const TEXT_LINE_HEIGHT = 1.12
const MAX_HEADLINE_LINES = 3
const BG_PAN_OVERSCAN = 1.12
const GRADIENT_DRIFT_DEG = 25
const FLOAT_AMPLITUDE = 0.025
const FLOAT_TILT = 0.012
const SLIDESHOW_TRANSITION_SHARE = 0.3
const SLIDESHOW_BLUR = 0.04
const BG_ZOOM_AMPLITUDE = 0.2
const SLIDE_AMPLITUDE = 0.1
const SUBLINE_WEIGHT = 400
const TEXT_EDGE_MARGIN = 0.04
const STRIP_GAP = 0.08
const GAMEPLAY_FADE = 0.65

/**
 * Canvas `fillText` does not trigger web-font loading, so load the weights the design can use before any draw
 * that matters (export, first preview). A blocked font just falls back.
 */
export const ensureCreativeFonts = async (): Promise<void> => {
  if (typeof document === 'undefined' || !document.fonts) return
  await Promise.all(
    [...Object.values(TEXT_WEIGHTS), SUBLINE_WEIGHT].map((weight) =>
      document.fonts.load(`${weight} 16px Roboto`).catch(() => undefined),
    ),
  )
  await document.fonts.ready
}

const imageSize = (img: HTMLImageElement): {w: number; h: number} => ({
  w: img.naturalWidth || img.width,
  h: img.naturalHeight || img.height,
})

/** 0 → 1 → 0 over one loop, so every animation ends exactly where it started. */
const pingPong = (t: number): number => 0.5 - 0.5 * Math.cos(2 * Math.PI * t)

/** 0 → 1 → 0, repeated `speed` times per loop. */
const cycle = (anim: CreativeAnimation<string>, t: number): number => pingPong((t * anim.speed) % 1)

/** sin over `speed` cycles per loop, optionally phase-shifted (fraction of a cycle). */
const wave = (anim: CreativeAnimation<string>, t: number, phase = 0): number =>
  Math.sin(2 * Math.PI * (t * anim.speed + phase))

/** Where zooms are anchored, as 0..1 pan positions (0.5 = centre). */
const CENTER_FOCUS = {x: 0.5, y: 0.5}
const ZOOM_FOCUS: Partial<Record<CreativeAnimationDirection, {x: number; y: number}>> = {
  center: CENTER_FOCUS,
  left: {x: 0, y: 0.5},
  right: {x: 1, y: 0.5},
  up: {x: 0.5, y: 0},
  down: {x: 0.5, y: 1},
}

/** Background "camera": pan 0..1 per axis (0.5 = centred) and zoom relative to cover-fit. */
interface CoverView {
  panX: number
  panY: number
  zoom: number
}

const STILL_VIEW: CoverView = {panX: 0.5, panY: 0.5, zoom: 1}

const drawCover = (
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
  view: CoverView = STILL_VIEW,
): void => {
  const {w: iw, h: ih} = imageSize(img)
  if (iw === 0 || ih === 0) return
  const scale = Math.max(w / iw, h / ih) * view.zoom
  const dw = iw * scale
  const dh = ih * scale
  ctx.drawImage(img, x - (dw - w) * view.panX, y - (dh - h) * view.panY, dw, dh)
}

const backgroundView = (frame: MotionFrame | undefined): CoverView | undefined => {
  const anim = frame?.animation.bg
  if (!frame || !anim || anim.type === 'none') return undefined
  const p = cycle(anim, frame.t)
  if (anim.type === 'zoom') {
    const focus = ZOOM_FOCUS[anim.direction] ?? CENTER_FOCUS
    return {panX: focus.x, panY: focus.y, zoom: 1 + BG_ZOOM_AMPLITUDE * p}
  }
  const along = anim.direction !== 'vertical'
  const down = anim.direction !== 'horizontal'
  return {panX: along ? p : 0.5, panY: down ? p : 0.5, zoom: BG_PAN_OVERSCAN}
}

const paintBackground = (
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  state: CreativeAssetState,
  frame?: MotionFrame,
): void => {
  ctx.fillStyle = FALLBACK_BACKGROUND
  ctx.fillRect(0, 0, w, h)
  if (state.bgMode === 'color') {
    ctx.fillStyle = state.bgColor
    ctx.fillRect(0, 0, w, h)
  } else if (state.bgMode === 'gradient') {
    const bgAnim = frame?.animation.bg
    const drift = frame && bgAnim?.type === 'pan' ? wave(bgAnim, frame.t) * GRADIENT_DRIFT_DEG : 0
    const angle = ((state.bgAngle + drift - 90) * Math.PI) / 180
    const half = Math.hypot(w, h) / 2
    const dx = Math.cos(angle) * half
    const dy = Math.sin(angle) * half
    const grad = ctx.createLinearGradient(w / 2 - dx, h / 2 - dy, w / 2 + dx, h / 2 + dy)
    grad.addColorStop(0, state.bgFrom)
    grad.addColorStop(1, state.bgTo)
    ctx.fillStyle = grad
    ctx.fillRect(0, 0, w, h)
  } else if (state.bgImage) {
    drawCover(ctx, state.bgImage, 0, 0, w, h, backgroundView(frame))
  }
}

const roundedRectPath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void => {
  ctx.beginPath()
  ctx.roundRect(x, y, w, h, r)
}

interface PlacedImage {
  img: HTMLImageElement
  cx: number
  cy: number
  w: number
  h: number
  rotation?: number
  radiusRatio: number
  shadow: number
}

/** Draws an image with rounded corners, optional rotation and a soft drop shadow. */
const drawRoundedImage = (ctx: CanvasRenderingContext2D, p: PlacedImage): void => {
  ctx.save()
  ctx.translate(p.cx, p.cy)
  if (p.rotation) ctx.rotate(p.rotation)
  const radius = Math.min(p.w, p.h) * p.radiusRatio
  if (p.shadow > 0) {
    ctx.shadowColor = 'rgba(0, 0, 0, 0.45)'
    ctx.shadowBlur = p.shadow
    ctx.shadowOffsetY = p.shadow * 0.35
    ctx.fillStyle = '#000000'
    roundedRectPath(ctx, -p.w / 2, -p.h / 2, p.w, p.h, radius)
    ctx.fill()
    ctx.shadowColor = 'transparent'
  }
  roundedRectPath(ctx, -p.w / 2, -p.h / 2, p.w, p.h, radius)
  ctx.clip()
  ctx.drawImage(p.img, -p.w / 2, -p.h / 2, p.w, p.h)
  ctx.restore()
}

/** Per-element bobbing for the 'float' motion; zero for every other motion / for stills. */
const floatOffset = (
  frame: MotionFrame | undefined,
  index: number,
  unit: number,
): {dx: number; dy: number; rot: number} => {
  const anim = frame?.animation.content
  if (!frame || anim?.type !== 'float') return {dx: 0, dy: 0, rot: 0}
  const angle = 2 * Math.PI * (frame.t * anim.speed + index * 0.17)
  const amp = FLOAT_AMPLITUDE * unit
  const sin = Math.sin(angle) * amp
  const rot = Math.sin(angle) * FLOAT_TILT
  if (anim.direction === 'horizontal') return {dx: sin, dy: 0, rot}
  if (anim.direction === 'circle') return {dx: Math.cos(angle) * amp, dy: sin, rot}
  return {dx: 0, dy: sin, rot}
}

const wrapLines = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
  const words = text.trim().split(/\s+/).filter(Boolean)
  const lines: string[] = []
  let current = ''
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word
    if (current && ctx.measureText(candidate).width > maxWidth) {
      lines.push(current)
      current = word
    } else {
      current = candidate
    }
  })
  if (current) lines.push(current)
  return lines
}

/** The template's text region: [left, left + width] horizontally, centred on centerY, with its preferred alignment. */
interface TextBlockOptions {
  left: number
  width: number
  centerY: number
  align: 'left' | 'center'
}

/** Headline + subline stacked and vertically centred on `centerY`; the headline shrinks to fit. */
const drawTextBlock = (
  ctx: CanvasRenderingContext2D,
  box: LayoutBox,
  state: CreativeAssetState,
  o: TextBlockOptions,
): void => {
  const headline = state.headline.trim()
  const subline = state.subline.trim()
  if (!headline && !subline) return

  let headSize = box.unit * HEADLINE_SIZE * state.headlineScale
  const headFont = (size: number): string => `${TEXT_WEIGHTS[state.headlineWeight]} ${size}px ${FONT_FAMILY}`
  let headLines: string[] = []
  if (headline) {
    do {
      ctx.font = headFont(headSize)
      headLines = wrapLines(ctx, headline, o.width)
      const widest = Math.max(...headLines.map((l) => ctx.measureText(l).width))
      if (headLines.length <= MAX_HEADLINE_LINES && widest <= o.width) break
      headSize *= 0.92
    } while (headSize > box.unit * 0.03)
  }
  const subSize = box.unit * SUBLINE_SIZE * state.sublineScale
  ctx.font = `${SUBLINE_WEIGHT} ${subSize}px ${FONT_FAMILY}`
  const subLines = subline ? wrapLines(ctx, subline, o.width).slice(0, 2) : []

  const gap = headLines.length && subLines.length ? headSize * 0.35 : 0
  const headHeight = headLines.length * headSize * TEXT_LINE_HEIGHT
  const subHeight = subLines.length * subSize * TEXT_LINE_HEIGHT
  const blockHeight = headHeight + gap + subHeight
  const margin = box.unit * TEXT_EDGE_MARGIN
  const vertical = {
    auto: o.centerY,
    middle: box.cy,
    top: box.cy - box.unit * 0.5 + blockHeight / 2 + margin,
    bottom: box.cy + box.unit * 0.5 - blockHeight / 2 - margin,
  }[state.textVertical]
  const align = state.textAlign === 'auto' ? o.align : state.textAlign
  const x = {left: o.left, center: o.left + o.width / 2, right: o.left + o.width}[align]
  let y = vertical - blockHeight / 2

  ctx.save()
  ctx.fillStyle = state.textColor
  ctx.textAlign = align
  ctx.textBaseline = 'top'
  ctx.shadowColor = 'rgba(0, 0, 0, 0.35)'
  ctx.shadowBlur = box.unit * 0.03
  ctx.font = headFont(headSize)
  headLines.forEach((line) => {
    ctx.fillText(line, x, y)
    y += headSize * TEXT_LINE_HEIGHT
  })
  y += gap
  ctx.globalAlpha = 0.85
  ctx.font = `${SUBLINE_WEIGHT} ${subSize}px ${FONT_FAMILY}`
  subLines.forEach((line) => {
    ctx.fillText(line, x, y)
    y += subSize * TEXT_LINE_HEIGHT
  })
  ctx.restore()
}

/** True when the design has something to export besides the background. */
export const hasCreativeContent = (state: CreativeAssetState): boolean =>
  state.keyArt !== null || state.screenshots.length > 0 || state.icon !== null || state.headline.trim() !== ''

const hasText = (state: CreativeAssetState): boolean => state.headline.trim() !== '' || state.subline.trim() !== ''

/** Left text column used by the templates that put copy beside the artwork. */
const drawSideText = (ctx: CanvasRenderingContext2D, box: LayoutBox, state: CreativeAssetState): void => {
  if (!hasText(state)) return
  drawTextBlock(ctx, box, state, {
    left: box.cx - box.unit * 0.95,
    width: box.unit * 0.95,
    centerY: box.cy,
    align: 'left',
  })
}

const drawCenterText = (
  ctx: CanvasRenderingContext2D,
  box: LayoutBox,
  state: CreativeAssetState,
  centerY: number,
): void => {
  drawTextBlock(ctx, box, state, {left: box.cx - box.unit * 0.9, width: box.unit * 1.8, centerY, align: 'center'})
}

/** Screenshot drawn at `height` (design units), width from its own aspect ratio. */
const drawShot = (
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  box: LayoutBox,
  x: number,
  y: number,
  height: number,
  rotation: number,
): void => {
  const {w: iw, h: ih} = imageSize(img)
  if (iw === 0 || ih === 0) return
  const h = box.unit * height
  drawRoundedImage(ctx, {
    img,
    cx: x,
    cy: y,
    w: (h * iw) / ih,
    h,
    rotation,
    radiusRatio: SCREENSHOT_CORNER_RATIO,
    shadow: box.unit * 0.06,
  })
}

const paintKeyArt: TemplatePainter = (ctx, box, state, frame) => {
  const img = state.keyArt
  if (img) {
    const {w: iw, h: ih} = imageSize(img)
    if (iw > 0 && ih > 0) {
      const scale = Math.min((2 * box.unit) / iw, box.unit / ih) * state.keyArtZoom
      const {dx, dy} = floatOffset(frame, 0, box.unit)
      ctx.drawImage(
        img,
        box.cx + state.keyArtOffsetX * box.unit - (iw * scale) / 2 + dx,
        box.cy + state.keyArtOffsetY * box.unit - (ih * scale) / 2 + dy,
        iw * scale,
        ih * scale,
      )
    }
  }
  drawCenterText(ctx, box, state, box.cy + box.unit * 0.36)
}

const FAN_LAYOUTS: Record<number, number[]> = {1: [0], 2: [-0.5, 0.5], 3: [-1, 0, 1]}

const paintFan: TemplatePainter = (ctx, box, state, frame) => {
  const withText = hasText(state)
  const fanCx = box.cx + (withText ? 0.5 : 0) * box.unit
  const shots = state.screenshots
  const slots = FAN_LAYOUTS[shots.length] ?? []
  // Side shots first so the centre one overlaps them.
  const order = slots.map((slot, i) => ({slot, img: shots[i], i})).sort((a, b) => Math.abs(b.slot) - Math.abs(a.slot))
  order.forEach(({slot, img, i}) => {
    const isSide = slot !== 0
    const float = floatOffset(frame, i, box.unit)
    drawShot(
      ctx,
      img,
      box,
      fanCx + slot * box.unit * 0.3 + float.dx,
      box.cy + (isSide ? 0.04 : 0) * box.unit + float.dy,
      isSide ? 0.88 : 0.98,
      slot * 0.1 + float.rot,
    )
  })
  drawSideText(ctx, box, state)
}

/** Diagonal staircase of up to three tilted screenshots, text on the left. */
const paintCascade: TemplatePainter = (ctx, box, state, frame) => {
  const shots = state.screenshots
  const cx = box.cx + (hasText(state) ? 0.5 : 0) * box.unit
  shots.forEach((img, i) => {
    const centered = i - (shots.length - 1) / 2
    const float = floatOffset(frame, i, box.unit)
    drawShot(
      ctx,
      img,
      box,
      cx + centered * box.unit * 0.36 + float.dx,
      box.cy - centered * box.unit * 0.08 + float.dy,
      0.86,
      -0.1 + float.rot,
    )
  })
  drawSideText(ctx, box, state)
}

/** Row of screenshots repeated across the whole canvas; in a video it scrolls endlessly (one row-period per cycle). */
const paintStrip: TemplatePainter = (ctx, box, state, frame) => {
  const shots = state.screenshots.filter((img) => imageSize(img).h > 0)
  if (shots.length === 0) return
  const withText = hasText(state)
  const height = withText ? 0.72 : 0.95
  const y = box.cy + (withText ? -0.12 : 0) * box.unit
  const widths = shots.map((img) => {
    const {w, h} = imageSize(img)
    return (height * w) / h
  })
  const period = widths.reduce((sum, w) => sum + w + STRIP_GAP, 0)
  const content = frame?.animation.content
  const scrollDir = content?.direction === 'right' ? 1 : -1
  const travelled = frame && content?.type === 'scroll' ? scrollDir * frame.t * content.speed * period : 0
  // Wrapped to one period so the row always covers the canvas, whatever the speed or direction.
  const offset = ((travelled % period) + period) % period
  // Visible range in design units, left and right of the safe-area centre.
  const left = -box.cx / box.unit
  const right = (box.canvasW - box.cx) / box.unit
  const base = -1 + offset
  const first = base - period * (Math.ceil((base - left) / period) + 1)
  const copies = Math.ceil((right - first) / period) + 1
  ctx.save()
  ctx.beginPath()
  ctx.rect(0, 0, box.canvasW, box.canvasH)
  ctx.clip()
  for (let k = 0; k < copies; k++) {
    let x = first + k * period
    shots.forEach((img, i) => {
      drawShot(ctx, img, box, box.cx + (x + widths[i] / 2) * box.unit, y, height, 0)
      x += widths[i] + STRIP_GAP
    })
  }
  ctx.restore()
  if (withText) drawCenterText(ctx, box, state, box.cy + box.unit * 0.38)
}

/** Big headline on the left, one rounded image (first screenshot, else key art) on the right. */
const paintSplitText: TemplatePainter = (ctx, box, state, frame) => {
  const img = state.screenshots[0] ?? state.keyArt
  const float = floatOffset(frame, 0, box.unit)
  if (img) drawShot(ctx, img, box, box.cx + box.unit * 0.5 + float.dx, box.cy + float.dy, 0.96, float.rot)
  drawSideText(ctx, box, state)
}

const smoothstep = (x: number): number => x * x * (3 - 2 * x)

/** One slideshow layer: a screenshot with its own horizontal offset, scale, opacity and blur. */
interface SlideLayer {
  img: HTMLImageElement
  dx: number
  scale: number
  alpha: number
  blur: number
}

const drawSlideLayer = (
  ctx: CanvasRenderingContext2D,
  box: LayoutBox,
  x: number,
  y: number,
  height: number,
  layer: SlideLayer,
): void => {
  if (layer.alpha <= 0) return
  ctx.save()
  ctx.globalAlpha = layer.alpha
  if (layer.blur > 0.5 && 'filter' in ctx) ctx.filter = `blur(${layer.blur}px)`
  drawShot(ctx, layer.img, box, x + layer.dx, y, height * layer.scale, 0)
  ctx.restore()
}

/** Outgoing + incoming layer for transition progress `p` (0 → 1) in the given style. */
const slideLayers = (
  style: CreativeAnimationDirection,
  from: HTMLImageElement,
  to: HTMLImageElement,
  p: number,
  box: LayoutBox,
): [SlideLayer, SlideLayer] => {
  // Far enough that the incoming screenshot enters from outside the canvas, never popping up mid-image.
  const distance = box.canvasW / 2 + box.unit
  const blur = box.unit * SLIDESHOW_BLUR * 8
  switch (style) {
    case 'blur':
      return [
        {img: from, dx: 0, scale: 1 + 0.04 * p, alpha: 1 - p, blur: blur * p},
        {img: to, dx: 0, scale: 1.04 - 0.04 * p, alpha: p, blur: blur * (1 - p)},
      ]
    case 'push':
      return [
        {img: from, dx: -distance * p, scale: 1, alpha: 1, blur: 0},
        {img: to, dx: distance * (1 - p), scale: 1, alpha: 1, blur: 0},
      ]
    case 'cover':
      return [
        {img: from, dx: -distance * 0.25 * p, scale: 1 - 0.08 * p, alpha: 1 - 0.6 * p, blur: 0},
        {img: to, dx: distance * (1 - p), scale: 1, alpha: 1, blur: 0},
      ]
    case 'scale':
      return [
        {img: from, dx: 0, scale: 1 + 0.25 * p, alpha: 1 - p, blur: 0},
        {img: to, dx: 0, scale: 0.75 + 0.25 * p, alpha: p, blur: 0},
      ]
    default:
      return [
        {img: from, dx: 0, scale: 1, alpha: 1 - p, blur: 0},
        {img: to, dx: 0, scale: 1, alpha: p, blur: 0},
      ]
  }
}

/**
 * Slideshow: one hero screenshot at a time, cycling through all screenshots with a transition.
 * `speed` full passes per loop, and the last screenshot hands back to the first, so the loop stays seamless.
 */
const paintSlideshow: TemplatePainter = (ctx, box, state, frame) => {
  const shots = state.screenshots.filter((img) => imageSize(img).h > 0)
  const anim = frame?.animation.content
  if (!frame || !anim || shots.length === 0) return
  const withText = hasText(state)
  const x = box.cx + (withText ? 0.5 : 0) * box.unit
  const height = 0.96
  const progress = (frame.t * anim.speed * shots.length) % shots.length
  const index = Math.floor(progress)
  const local = progress - index
  const hold = 1 - SLIDESHOW_TRANSITION_SHARE
  const p = local < hold ? 0 : smoothstep((local - hold) / SLIDESHOW_TRANSITION_SHARE)
  const from = shots[index]
  const to = shots[(index + 1) % shots.length]
  ctx.save()
  ctx.beginPath()
  ctx.rect(0, 0, box.canvasW, box.canvasH)
  ctx.clip()
  const y = box.cy
  if (shots.length === 1 || p === 0) {
    drawSlideLayer(ctx, box, x, y, height, {img: from, dx: 0, scale: 1, alpha: 1, blur: 0})
  } else {
    slideLayers(anim.direction, from, to, p, box).forEach((layer) => drawSlideLayer(ctx, box, x, y, height, layer))
  }
  ctx.restore()
  drawSideText(ctx, box, state)
}

const paintIconHeadline: TemplatePainter = (ctx, box, state, frame) => {
  const iconSize = box.unit * 0.62
  const float = floatOffset(frame, 0, box.unit)
  if (state.icon) {
    drawRoundedImage(ctx, {
      img: state.icon,
      cx: box.cx - box.unit * (hasText(state) ? 0.62 : 0) + float.dx,
      cy: box.cy + float.dy,
      w: iconSize,
      h: iconSize,
      rotation: float.rot,
      radiusRatio: ICON_CORNER_RATIO,
      shadow: box.unit * 0.05,
    })
  }
  if (!hasText(state)) return
  if (state.icon) {
    drawTextBlock(ctx, box, state, {
      left: box.cx - box.unit * 0.2,
      width: box.unit * 1.15,
      centerY: box.cy,
      align: 'left',
    })
  } else {
    drawCenterText(ctx, box, state, box.cy)
  }
}

/** Icon on top, centred headline below — a compact vertical lockup. */
const paintIconStack: TemplatePainter = (ctx, box, state, frame) => {
  const float = floatOffset(frame, 0, box.unit)
  const size = box.unit * 0.46
  if (state.icon) {
    drawRoundedImage(ctx, {
      img: state.icon,
      cx: box.cx + float.dx,
      cy: box.cy - box.unit * (hasText(state) ? 0.2 : 0) + float.dy,
      w: size,
      h: size,
      rotation: float.rot,
      radiusRatio: ICON_CORNER_RATIO,
      shadow: box.unit * 0.05,
    })
  }
  drawCenterText(ctx, box, state, box.cy + box.unit * (state.icon ? 0.28 : 0))
}

/** Full-bleed image (cover-fit, centred so the focal point stays in the safe area) with the copy inside the safe area. */
const paintGameplay: TemplatePainter = (ctx, box, state) => {
  const img = state.screenshots[0] ?? state.keyArt
  if (img) {
    drawCover(ctx, img, 0, 0, box.canvasW, box.canvasH)
    if (hasText(state)) {
      const fade = ctx.createLinearGradient(0, box.cy, 0, box.cy + box.unit * 0.5)
      fade.addColorStop(0, 'rgba(0, 0, 0, 0)')
      fade.addColorStop(1, `rgba(0, 0, 0, ${GAMEPLAY_FADE})`)
      ctx.fillStyle = fade
      ctx.fillRect(0, 0, box.canvasW, box.canvasH)
    }
  }
  drawTextBlock(ctx, box, state, {
    left: box.cx - box.unit * 0.9,
    width: box.unit * 1.8,
    centerY: box.cy + box.unit * 0.3,
    align: 'left',
  })
}

const TEMPLATE_PAINTERS: Record<CreativeTemplateId, TemplatePainter> = {
  keyArt: paintKeyArt,
  fan: paintFan,
  cascade: paintCascade,
  strip: paintStrip,
  splitText: paintSplitText,
  iconHeadline: paintIconHeadline,
  iconStack: paintIconStack,
  gameplay: paintGameplay,
}

/** The design box is the placement's safe area (scaled), so one design survives every crop. */
const layoutBoxFor = (placementId: CreativePlacementId, scale: number): LayoutBox => {
  const {width, height, safeArea} = getPlacementSpec(placementId)
  return {
    canvasW: Math.round(width * scale),
    canvasH: Math.round(height * scale),
    cx: (width * scale) / 2,
    cy: (height * scale) / 2 + safeArea.offsetY * scale,
    unit: Math.min((safeArea.width * scale) / 2, safeArea.height * scale) * SAFE_AREA_FILL,
  }
}

/**
 * Renders one placement. `scale` 1 = the exact App Store Connect pixel size; below 1 = preview.
 * Pass `frame` to render a point of the video loop, and `target` to reuse a canvas across frames.
 */
export const composeCreativeAsset = (
  state: CreativeAssetState,
  placementId: CreativePlacementId,
  scale: number,
  frame?: MotionFrame,
  target?: HTMLCanvasElement,
): HTMLCanvasElement => {
  const spec = getPlacementSpec(placementId)
  const canvas = target ?? document.createElement('canvas')
  const w = Math.round(spec.width * scale)
  const h = Math.round(spec.height * scale)
  if (canvas.width !== w) canvas.width = w
  if (canvas.height !== h) canvas.height = h
  const ctx = canvas.getContext('2d', {alpha: false})
  if (!ctx) return canvas
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  paintBackground(ctx, w, h, state, frame)
  const box = layoutBoxFor(placementId, scale)
  ctx.save()
  const content = frame?.animation.content
  if (frame && content?.type === 'slide') {
    const shift = wave(content, frame.t) * SLIDE_AMPLITUDE * box.unit
    const dx = content.direction === 'vertical' ? 0 : shift
    const dy = content.direction === 'horizontal' ? 0 : content.direction === 'diagonal' ? shift * 0.5 : shift
    ctx.translate(dx, dy)
  }
  const isSlideshow =
    frame &&
    content?.type === 'slideshow' &&
    SLIDESHOW_TEMPLATES.includes(state.template) &&
    state.screenshots.length > 0
  const paint = isSlideshow ? paintSlideshow : TEMPLATE_PAINTERS[state.template]
  paint(ctx, box, state, frame)
  ctx.restore()
  return canvas
}

const canvasToJpegBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error('canvas.toBlob returned null'))
      },
      'image/jpeg',
      JPEG_EXPORT_QUALITY,
    )
  })

/** Format actually used for a placement — Universal is PNG only. */
export const resolveFormat = (placementId: CreativePlacementId, format: CreativeImageFormat): CreativeImageFormat =>
  getPlacementSpec(placementId).formats.includes(format) ? format : 'png'

export const creativeFileName = (placementId: CreativePlacementId, format: CreativeImageFormat): string => {
  const spec = getPlacementSpec(placementId)
  const ext = resolveFormat(placementId, format) === 'jpeg' ? 'jpg' : 'png'
  return `${spec.fileBase}-${spec.width}x${spec.height}.${ext}`
}

/** Full-size render of one placement; the canvas is released right after encoding (iOS canvas memory cap). */
const renderPlacementBlob = async (
  state: CreativeAssetState,
  placementId: CreativePlacementId,
  format: CreativeImageFormat,
): Promise<Blob> => {
  const canvas = composeCreativeAsset(state, placementId, 1)
  try {
    return resolveFormat(placementId, format) === 'jpeg'
      ? await canvasToJpegBlob(canvas)
      : await canvasToOpaquePng(canvas)
  } finally {
    canvas.width = 0
    canvas.height = 0
  }
}

/** Renders all three placements one at a time and packs them into a ZIP. */
export const generateCreativeAssetZip = async (
  state: CreativeAssetState,
  format: CreativeImageFormat,
): Promise<Blob> => {
  await ensureCreativeFonts()
  const zip = new JSZip()
  for (const spec of CREATIVE_PLACEMENTS) {
    zip.file(creativeFileName(spec.id, format), await renderPlacementBlob(state, spec.id, format))
  }
  return zip.generateAsync({type: 'blob'})
}
